import React, { useState, useEffect } from 'react'
import ChatInterface from './components/ChatInterface'
import ConfigPanel from './components/ConfigPanel'
import AdminPanel from './components/AdminPanel'
import EnvironmentDiagnostic from './components/EnvironmentDiagnostic'
import { ApiClient } from './services/api-client'
import { adminApi} from './services/AdminApiService'
import { useConversationHistory } from './hooks/useConversationHistory'
import { ChatMessage, SimulatorConfig, ConversationStats, FileData, DebugMessage } from './types/chat'
import { ConversationMessage } from './types/conversationHistory'
import appConfig from './config/AppConfig'

const App: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDebugMode, setIsDebugMode] = useState(appConfig.debugMode)
  const [showAdminPanel, setShowAdminPanel] = useState(false)
  const [showDiagnostic, setShowDiagnostic] = useState(appConfig.debugMode)
  const [conversationStats, setConversationStats] = useState<ConversationStats>({
    totalTokens: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalCost: 0,
    messageCount: 0,
    tags: []
  })
  const [config, setConfig] = useState<SimulatorConfig>({
    phoneNumber: '647723491',
    apiUrl: appConfig.apiLocalUrl
  })
  
  const [apiClient, setApiClient] = useState(new ApiClient(config.apiUrl))
  const conversationHistory = useConversationHistory(config.apiUrl)

  useEffect(() => {
    setApiClient(new ApiClient(config.apiUrl))
    checkConnection()
  }, [config.apiUrl])

  useEffect(() => {
    const userAndBotMessages = messages.filter(msg => msg.sender === 'user' || msg.sender === 'bot')
    setConversationStats(prev => ({
      ...prev,
      messageCount: userAndBotMessages.length
    }))
  }, [messages])

  useEffect(() => {
    const isLocalhost = config.apiUrl.includes('localhost')
    setIsDebugMode(isLocalhost || appConfig.debugMode)
  }, [config.apiUrl])

  useEffect(() => {
    if (config.phoneNumber && config.phoneNumber.trim()) {
      fetchCurrentTag()
    }
  }, [config.phoneNumber])

  const fetchCurrentTag = async () => {
    try {
      const tagInfo = await adminApi.getCurrentClientTag(config.phoneNumber)
      if (tagInfo.success && tagInfo.tag) {
        setConversationStats(prev => ({
          ...prev,
          tags: [tagInfo.tag].filter((tag): tag is string => tag !== null)
        }))
      } else {
        setConversationStats(prev => ({
          ...prev,
          tags: []
        }))
      }
    } catch (error) {
      console.error('Error fetching current tag:', error)
      setConversationStats(prev => ({
        ...prev,
        tags: []
      }))
    }
  }

  const loadConversationStats = async (phone: string) => {
    try {
      const statsResponse = await adminApi.getConversationTokenStats(phone)
      if (statsResponse.success) {
        const stats = statsResponse.stats
        setConversationStats({
          totalTokens: stats.totalTokens,
          inputTokens: stats.inputTokens,
          outputTokens: stats.outputTokens,
          totalCost: stats.totalCost,
          messageCount: stats.messageCount,
          tags: stats.tag ? [stats.tag] : []
        })
        
        const statsMessage: ChatMessage = {
          id: Date.now().toString(),
          content: `📊 Estadísticas cargadas: ${stats.totalTokens} tokens (€${stats.totalCost.toFixed(4)}) | Etiqueta: ${stats.tag || 'Sin etiqueta'}`,
          sender: 'system',
          timestamp: new Date()
        }
        setMessages(prev => [...prev, statsMessage])
      }
    } catch (error) {
      console.error('Error loading conversation stats:', error)
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        content: '⚠️ No se pudieron cargar las estadísticas de la conversación',
        sender: 'system',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    }
  }

  const checkConnection = async () => {
    try {
      const response = await apiClient.healthCheck()
      setIsConnected(response.status === 'ok')
    } catch {
      setIsConnected(false)
    }
  }

  const consolidateToolCalls = (debugMessages: DebugMessage[]): DebugMessage[] => {
    const consolidated: { [key: string]: DebugMessage } = {}
    
    for (const debug of debugMessages) {
      if (debug.type === 'tool_call' && debug.toolName) {
        const key = `${debug.toolName}_${debug.mcpType}`
        
        if (!consolidated[key] || (debug.success !== undefined && consolidated[key].success === undefined)) {
          consolidated[key] = debug
        }
      } else {
        consolidated[`${debug.type}_${Date.now()}_${Math.random()}`] = debug
      }
    }
    
    return Object.values(consolidated)
  }

  const updateTokenStats = (response: any) => {
    const inputTokens = response.inputTokens || 0
    const outputTokens = response.outputTokens || 0
    const totalTokens = response.tokensUsed || (inputTokens + outputTokens)
    const cost = response.cost || 0

    if (isDebugMode) {
      console.log('🔢 Token Stats Update:', {
        received: {
          inputTokens: response.inputTokens,
          outputTokens: response.outputTokens,
          tokensUsed: response.tokensUsed,
          cost: response.cost
        },
        processed: { inputTokens, outputTokens, totalTokens, cost }
      })
    }

    setConversationStats(prev => ({
      ...prev,
      totalTokens: prev.totalTokens + totalTokens,
      inputTokens: prev.inputTokens + inputTokens,
      outputTokens: prev.outputTokens + outputTokens,
      totalCost: prev.totalCost + cost
    }))
  }

  const addDebugMessages = (debugMessages?: DebugMessage[]) => {
    if (!isDebugMode || !debugMessages?.length) return

    const consolidatedMessages = consolidateToolCalls(debugMessages)
    
    const debugSystemMessages: ChatMessage[] = consolidatedMessages.map(debug => ({
      id: `debug-${Date.now()}-${Math.random()}`,
      content: debug.message,
      sender: 'system',
      timestamp: new Date(debug.timestamp),
      debugInfo: {
        type: debug.type,
        toolName: debug.toolName,
        mcpType: debug.mcpType,
        executionTime: debug.executionTime,
        success: debug.success,
        error: debug.error,
        promptData: debug.promptData
      }
    }))

    setMessages(prev => [...prev, ...debugSystemMessages])
  }

  const convertHistoryToChat = (historyMessages: ConversationMessage[]): ChatMessage[] => {
    return historyMessages.map(msg => ({
      id: `history-${msg.id}`,
      content: msg.content,
      sender: msg.type === 'user' ? 'user' : msg.type === 'bot' ? 'bot' : 'system',
      timestamp: msg.timestamp,
      debugInfo: msg.type === 'tool' ? {
        type: 'tool_call',
        toolName: msg.metadata?.toolName || 'unknown',
        mcpType: 'history',
        success: true
      } : msg.type === 'summary' ? {
        type: 'system',
        mcpType: 'history'
      } : undefined,
      isHistorical: true
    }))
  }

  const handleSendMessage = async (
    content: string, 
    type: 'text' | 'audio' | 'document' | 'image' = 'text',
    fileData?: FileData
  ) => {
    if (!content.trim()) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content,
      sender: 'user',
      timestamp: new Date(),
      type,
      fileData
    }

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)

    try {
      const response = await apiClient.sendMessage({
        from: config.phoneNumber,
        body: content,
        type,
        fileData
      })

      if (isDebugMode) {
        console.log('📨 API Response:', response)
      }

      addDebugMessages(response.debugMessages)

      if (response.success && response.response) {
        const botMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: response.response,
          sender: 'bot',
          timestamp: new Date()
        }
        setMessages(prev => [...prev, botMessage])
        updateTokenStats(response)
        setTimeout(() => fetchCurrentTag(), 500)
      }
    } catch (error) {
      console.error('❌ API Error:', error)
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: 'Error: No se pudo conectar con el servidor',
        sender: 'system',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleStartConversation = async () => {
    if (!config.phoneNumber.trim()) return

    const today = new Date()
    const formattedDate = today.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    const proactiveMessage: ChatMessage = {
      id: Date.now().toString(),
      content: `🤖 Iniciando conversación proactiva para ${config.phoneNumber} (${formattedDate})`,
      sender: 'system',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, proactiveMessage])
    setIsLoading(true)

    try {
      const response = await apiClient.sendProactiveMessage(config.phoneNumber)

      if (isDebugMode) {
        console.log('📨 Proactive Response:', response)
      }

      addDebugMessages(response.debugMessages)

      if (response.success && response.response) {
        const botMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: response.response,
          sender: 'bot',
          timestamp: new Date()
        }
        setMessages(prev => [...prev, botMessage])
        updateTokenStats(response)
        setTimeout(() => fetchCurrentTag(), 500)
      }
    } catch (error) {
      console.error('❌ Proactive Error:', error)
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: 'Error: No se pudo iniciar la conversación proactiva',
        sender: 'system',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetSession = async () => {
    if (!config.phoneNumber.trim()) return

    const confirmReset = window.confirm(
      '⚠️ ATENCIÓN: Esto eliminará COMPLETAMENTE todos los datos del cliente:\\n\\n' +
      '• Conversaciones\\n' +
      '• Historial de mensajes\\n' +
      '• Información personal\\n' +
      '• Preferencias guardadas\\n' +
      '• Viajes consultados\\n' +
      '• TODO el registro del cliente\\n\\n' +
      '¿Estás seguro de que quieres continuar?'
    )

    if (!confirmReset) return

    try {
      await apiClient.resetContext(config.phoneNumber)
      
      setMessages([])
      setConversationStats({
        totalTokens: 0,
        inputTokens: 0,
        outputTokens: 0,
        totalCost: 0,
        messageCount: 0,
        tags: []
      })
      conversationHistory.clearHistory()

      const successMessage: ChatMessage = {
        id: Date.now().toString(),
        content: '✅ Reset completo realizado. Todos los datos del cliente han sido eliminados.',
        sender: 'system',
        timestamp: new Date()
      }
      setMessages([successMessage])

      if (isDebugMode) {
        console.log('🔄 Complete reset executed - all client data deleted')
      }
    } catch (error) {
      console.error('Error executing complete reset:', error)
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        content: '❌ Error: No se pudo ejecutar el reset completo',
        sender: 'system',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    }
  }

  const handleLoadConversation = async (phoneNumber?: string) => {
    
    const targetPhone = phoneNumber || config.phoneNumber
    
    if (!targetPhone.trim() || conversationHistory.isLoading) return

    setMessages([])
    const result = await conversationHistory.loadHistory(targetPhone)
    
    if (result && result.messages.length > 0) {
      const historicalMessages = convertHistoryToChat(result.messages)
      setMessages(historicalMessages)
      
      const loadMessage: ChatMessage = {
        id: Date.now().toString(),
        content: `📚 Historial cargado: ${result.messages.length} mensajes encontrados`,
        sender: 'system',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, loadMessage])
    } else if (conversationHistory.error) {
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        content: `❌ ${conversationHistory.error}`,
        sender: 'system',
        timestamp: new Date()
      }
      setMessages([errorMessage])
    } else {
      const noHistoryMessage: ChatMessage = {
        id: Date.now().toString(),
        content: '📚 No se encontró historial para este número',
        sender: 'system',
        timestamp: new Date()
      }
      setMessages([noHistoryMessage])
    }
  }

  const handleDebugModeChange = (enabled: boolean) => {
    setIsDebugMode(enabled)
  }

  const handleLoadChat = async (phone: string) => {
    setConfig(prev => ({ ...prev, phoneNumber: phone }))
    setShowAdminPanel(false)
    setMessages([])
    
    const loadingMessage: ChatMessage = {
      id: Date.now().toString(),
      content: `📱 Cargando conversación para ${phone}...`,
      sender: 'system',
      timestamp: new Date()
    }
    setMessages([loadingMessage])
    
    setTimeout(async () => {
      await Promise.all([
        handleLoadConversation(phone),
        loadConversationStats(phone)
      ])
    }, 100)
  }

  if (showAdminPanel) {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h1>{appConfig.appName} - Admin Panel</h1>
          <button 
            className="back-to-chat"
            onClick={() => setShowAdminPanel(false)}
          >
            ← Volver al Chat
          </button>
        </div>
        <AdminPanel onLoadChat={handleLoadChat} />
        
        <style>{`
          .admin-container {
            min-height: 100vh;
            background: #f5f5f5;
            padding: 20px;
          }
          
          .admin-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding: 0 20px;
          }
          
          .admin-header h1 {
            margin: 0;
            color: #333;
            font-size: 28px;
          }
          
          .back-to-chat {
            padding: 10px 20px;
            background: #2196F3;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: background 0.2s;
          }
          
          .back-to-chat:hover {
            background: #1976D2;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="chat-container">
      {showDiagnostic && <EnvironmentDiagnostic />}
      
      <div className="chat-layout">
        <ConfigPanel
          config={config}
          isConnected={isConnected}
          isDebugMode={isDebugMode}
          conversationStats={conversationStats}
          onConfigChange={setConfig}
          onDebugModeChange={handleDebugModeChange}
          onResetSession={handleResetSession}
          onCheckConnection={checkConnection}
          onLoadConversation={handleLoadConversation}
          onStartConversation={handleStartConversation}
          onOpenAdminPanel={() => setShowAdminPanel(true)}
          conversationHistory={conversationHistory}
        />
        <ChatInterface
          messages={messages}
          isLoading={isLoading}
          isDebugMode={isDebugMode}
          onSendMessage={handleSendMessage}
        />
      </div>
      
      {appConfig.debugMode && (
        <button 
          onClick={() => setShowDiagnostic(!showDiagnostic)}
          style={{ 
            position: 'fixed', 
            top: '10px', 
            right: '10px', 
            zIndex: 10000,
            padding: '5px 10px',
            background: 'orange',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          {showDiagnostic ? 'Hide' : 'Show'} Debug
        </button>
      )}
      
      <style>{`
        .chat-container {
          height: 100vh;
        }
        
        .chat-layout {
          display: flex;
          height: 100vh;
        }
      `}</style>
    </div>
  )
}

export default App
